<template>
  <div class="details-component">
    <div class="details-component__header">

      <h1>{{ $t('common.products') }}</h1>

      <div class="details-component__header_right">

        <a v-if="!isProductFormOpened" class="link" v-on:click="toggleProductForm">
          {{ $t('products.add_product') }}
          <i class="icon icon--plus"></i>
        </a>

        <a v-else class="disabled link" v-on:click="toggleProductForm" v-bind:class="{ active: isActive }">
          {{ $t('common.close') }}
          <i class="icon icon--cross"></i>
        </a>

      </div>

    </div>

    <autocomplete class="details-component__lookupform inline-search" lookup="products"
      :filter="autocompleteProductFilter" v-on:query="selectProduct" v-bind:class="{ active: isActive }"
      v-if="isProductFormOpened">
      <template slot="inner-item" slot-scope="props">
        <span>{{ props.item.name }}</span>
        <span>
          <span class="disabled">{{ props.item.total }}/</span>
          <span>{{ props.item.issued }}</span>
        </span>
      </template>
    </autocomplete>

    <div class="details-component__lookupform details-component__lookupform-confirm"
      v-if="isProductFormOpened && product !== null" v-bind:class="{ active: isActive }">
      <form class="form">

        <div class="form__field">
          <label class="form__label">{{ $t('common.product') }}</label>
          <div>{{ product.name }}</div>
        </div>

        <div class="form__field" :class="{ 'form__field_error': errors.has('amount') }"
          v-if="product.type === 'permitPack'">
          <label for="amount" class="form__label">{{ $t('common.amount') }}</label>
          <input type="text" id="amount" class="form__input" v-model="form.amount" v-validate
            data-vv-rules="required|numeric|max:4|cannotBeLower" name="amount" ref="amount" />
        </div>

        <div class="form__buttons buttonset">
          <button type="button" class="buttonset__button btn btn--secondary"
            v-on:click.prevent.stop="toggleProductForm()">
            {{ $t('common.discard') }}
          </button>
          <button type="submit" class="buttonset__button btn btn--main" v-on:click.prevent.stop="addProduct()">
            {{ $t('products.submit') }}
          </button>
        </div>
      </form>
    </div>

    <div class="details-component__content content" v-if="products">
      <v-client-table class="mobile-1070" :data="products" :columns="columns" :options="options">
        <!-- Type -->
        <span slot="type" slot-scope="props" class="mobile-padding">
          <span class="mobile">{{ $t('products.type') }}: </span>
          <span class="total">{{ $t(`product.${props.row.type}`) }}</span>
        </span>
        <!-- Name -->
        <span slot="name" slot-scope="props" class="mobile-padding">
          <span class="mobile">{{ $t('common.name') }}: </span>
          <a class="link" @click="routeToProduct(props.row)">
            <span>{{ props.row.name }}</span>
          </a>
        </span>
        <span slot="total" slot-scope="props" class="total" v-show="props.row.type === 'permitPack'">
          {{ props.row.total }}
        </span>
        <!-- Zones  -->
        <span slot="zone" slot-scope="props">
          <span v-for="(zone, index) in props.row.zones" class="mobile-padding">
            <span class="mobile">{{ $t('common.zone') }}: </span>
            <span>{{ zone.code }}
              <span v-show="isLengthSmaller(index, props.row.zones.length)">,&nbsp;</span>
            </span>
          </span>
        </span>
        <!--  Available -->
        <span slot="available" slot-scope="props" class="mobile-padding">
          <span class="mobile" v-show="props.row.type === 'permitPack'">{{ $t('products.parking_spots') }}: </span>
          <span v-show="props.row.type === 'permitPack'">
            <span class="total" v-show="props.row.available >= 0">{{ props.row.available }}</span>
            <span class="total orange" v-show="props.row.available < 0">
              {{ $t('products.exceeded_by', { amount: Math.abs(props.row.available) }) }}
            </span>
          </span>
        </span>
        <!-- Issued -->
        <div slot="issued" slot-scope="props" v-show="props.row.type === 'permitPack'" class="mobile-padding">
          <span class="mobile">{{ $t('common.issued') }}: </span>
          <span class="total-permit-count" v-show="!isSelectedRow(`${props.row.id}-issued`)"
            @click="setSelectedRow(props.row, '-issued')">
            {{ props.row.issued }}
            <a class="edit-wrapper">
              {{ $t('common.change') }}
              <i class="icon icon--arrow" />
            </a>
          </span>
          <form class="form" v-show="isSelectedRow(`${props.row.id}-issued`)">
            <input @input="e => editTotalPermitAmount('permitInput', e.target.value)" name="permit_input"
              class="form__input--editing shortInput" :value="props.row.issued"
              :ref="'changeIssued' + props.row.id + '-issued'" @keydown.prevent.stop.enter="submitEdit(props.row.id)" />
            <div class="form__submit-edit" @click="submitEdit(props.row.id, props.row)"></div>
          </form>
        </div>
        <!-- Pool parking limit -->
        <div slot="pool_parking" slot-scope="props" v-if="user.poolParkingEnabled"
          v-show="props.row.type === 'permitPack'" class="mobile-padding">
          <span class="mobile">{{ $t('common.pool_parking_limit') }}: </span>
          <span class="total-permit-count" v-show="!isSelectedRow(`${props.row.id}-pool_parking`)"
            @click="setSelectedRow(props.row, '-pool_parking')">
            {{ props.row.pool_parking }}
            <a class="edit-wrapper">
              {{ $t('common.change') }}
              <i class="icon icon--arrow" />
            </a>
          </span>
          <form class="form" v-show="isSelectedRow(`${props.row.id}-pool_parking`)">
            <input @input="e => editPoolParkingLimitExceedingAmount('poolParkingInput', e.target.value)"
              name="pool_parking" class="form__input--editing shortInput" :value="props.row.pool_parking"
              :ref="'changePoolParking' + props.row.id + '-pool_parking'"
              @keydown.prevent.stop.enter="submitEdit(props.row.id)" />
            <div class="form__submit-edit" @click="submitEdit(props.row.id, props.row)"></div>
          </form>
        </div>
        <!-- Tenant pays for himself  -->
        <div slot="tenant_pays_pp" slot-scope="props" v-if="user.poolParkingEnabled"
          v-show="props.row.type === 'permitPack'" class="mobile-padding">
          <span class="mobile">{{ $t('common.tenant_pays_pp') }}: </span>
          <span class="total-permit-count span-in-row" v-show="!isSelectedRow(`${props.row.id}-tenant_pays_pp`)"
            @click="setSelectedRow(props.row, '-tenant_pays_pp')">
            <span v-show="props.row.tenant_pays_pp" class="tenantPays"></span>
            <span>
              <a class="edit-wrapper">
                {{ $t('common.change') }}
                <i class="icon icon--arrow" />
              </a>
            </span>
          </span>
          <form class="form form-in-row" v-show="isSelectedRow(`${props.row.id}-tenant_pays_pp`)">
            <span>
              <input type="checkbox" :name="`${props.row.id}-tenant_pays_pp`"
                :checked="Boolean(props.row.tenant_pays_pp)"
                :ref="'changeTenantPaysPp' + props.row.id + '-tenant_pays_pp'" value="true"
                @click="(event) => (editTenantPays(event.target.checked))" />
            </span>
            <span class="form__submit-edit" @click="submitEdit(props.row.id, props.row)"></span>
          </form>
        </div>
        <!--  Delete -->
        <div slot="remove_product" slot-scope="props" class="delete-wrapper">
          <a @click="showConfirmationForRemovingProduct(props.row)" :id="props.row.id">
            <span class="delete-wrapper__icon icon icon--cross"></span>
          </a>
        </div>
      </v-client-table>
    </div>

    <ConfirmationModal v-if="showConfirmationModal" @cancel="cancelProductRemove" @confirmed="commitRemoveProduct">
      <h1 slot="header">{{ $t('tenants.removing') }} {{ removeProduct.name }} </h1>
      <p slot="body">{{ $t('products.confirm_remove') }}</p>
    </ConfirmationModal>

    <WarningModal v-if="showWarningModal" @close="closeWarningModal">
      <h1 slot="header">{{ productWarningHeader }}</h1>
      <p slot="body">{{ productWarningBody }}</p>
    </WarningModal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import * as mTypes from 'store/mutation-types';
import ListWidget from 'common/ListWidget';
import Autocomplete from 'common/Autocomplete';
import Tooltip from 'common/Tooltip';
import ConfirmationModal from 'common/ConfirmationModal';
import WarningModal from 'common/WarningModal';

export default {
  props: ['products'],
  components: {
    ListWidget,
    Autocomplete,
    Tooltip,
    ConfirmationModal,
    WarningModal,
  },
  computed: mapGetters({
    tenant: 'tenants/tenant/tenant',
    tenantName: 'tenants/tenant/name',
    productsError: 'tenants/product/error',
    user: 'user/user/user',
    tenantsLimit: 'landlord/tenants/limit',
    keywordFilter: 'landlord/tenants/keywordFilter',
  }),
  created() {
    this.$validator.remove('cannotBeLower');
    this.createAmountValidator();
  },
  methods: {
    toggleProductForm() {
      this.isProductFormOpened = !this.isProductFormOpened;
      this.isActive = !this.isActive;
      if (this.isProductFormOpened) {
        this.$nextTick(() => {
          this.$el.getElementsByClassName('autocomplete__input')[0].focus();
        });
      } else {
        this.product = null;
        this.form.amount = null;
      }
    },
    autocompleteProductFilter(data) {
      if (!this.isSuperAdmin()) {
        return data;
      }
      return data.filter(value => value.partner_id === this.tenant.landlord.id);
    },
    selectProduct(q) {
      this.product = q.item;
      this.$nextTick(() => {
        if (this.product.type === 'permitPack') {
          this.$refs.amount.focus();
        }
      });
    },
    addProduct() {
      this.$validator.validateAll()
        .then((success) => {
          if (!success) {
            return;
          }
          const amount = parseInt(this.form.amount, 10);
          const tenantId = parseInt(this.$route.params.id, 10);
          this.$store.dispatch('tenants/product/add', {
            tenant_id: tenantId,
            product_id: this.product.id,
            amount,
            user: this.user,
          })
            .then(() => {
              if (this.productsError) {
                this.displayError();
              } else {
                this.fetchTenant();
                this.fetchTenantList();
                this.toggleProductForm();
              }
            }).catch(() => {
              if (this.productsError) {
                this.displayError();
              }
            });
        });
    },
    commitRemoveProduct() {
      this.$store.dispatch('tenants/product/delete', {
        tenant_id: this.$route.params.id,
        product_id: this.removeProduct.id,
        user: this.user,
      }).then(() => {
        if (this.productsError) {
          this.displayError();
        }
      }).catch(() => {
        if (this.productsError) {
          this.displayError();
        }
      });
      this.showConfirmationModal = false;
      this.fetchTenant();
      this.fetchTenantList();
      this.isRemoved = true;
      this.removeProduct = null;
    },
    cancelProductRemove() {
      this.showConfirmationModal = false;
      this.removeProduct = null;
    },
    isEditingIssued() {
      this.isEditingTenant = !this.isEditingTenant;
    },
    showConfirmationForRemovingProduct(product) {
      this.removeProduct = product;
      this.showConfirmationModal = true;
    },
    closeWarningModal() {
      this.showWarningModal = false;
      this.$store.commit(`tenants/product/${mTypes.DATA_ERROR}`, false);
    },
    submitEdit(productId, row = null) {
      /**
       * Check for value changed.
       */

      // Issued.
      if (this.isSelectedRow(`${productId}-issued`)
        && this.oldValue.toString() === this.$refs[`changeIssued${productId}-issued`].value) {
        this.setSelectedRow(null);
        return false;
      } else {
        this.permitInput = this.$refs[`changeIssued${productId}-issued`].value
      }
      // Pool parking limit.
      if (this.isSelectedRow(`${productId}-pool_parking`)
        && this.oldPoolParkingValue.toString() === this.$refs[`changePoolParking${productId}-pool_parking`].value) {
        this.setSelectedRow(null);
        return false;
      } else {
        this.poolParkingInput =
          this.$refs[`changePoolParking${productId}-pool_parking`] !== undefined
            ? this.$refs[`changePoolParking${productId}-pool_parking`].value
            : this.poolParkingInput
      }
      // Tenant pays.
      if (this.isSelectedRow(`${productId}-tenant_pays_pp`)) {
        if (this.oldTenantPaysPpValue === this.tenantPaysPpInput) {
          this.setSelectedRow(null);
          return false;
        }
      } else {
        this.tenantPaysPpInput = row.tenant_pays_pp
      }

      /**
       * Validation and update.
       */

      this.$validator.validateAll()
        .then((success) => {
          if (!success) {
            return;
          }
          const tenantId = parseInt(this.$route.params.id, 10);
          this.$store.dispatch('tenants/product/edit', {
            productId,
            companyId: tenantId,
            amount: this.permitInput,
            pool_parking: this.poolParkingInput,
            tenant_pays_pp: this.tenantPaysPpInput,
            user: this.user,
          })
            .then(() => {
              if (this.productsError) {
                this.displayError();
              } else {
                this.fetchTenant();
                this.fetchTenantList();
              }
            })
            .catch(() => { });
        });
      this.setSelectedRow(null);
      return false;
    },
    displayError() {
      this.productWarningHeader = this.$t('common.error');
      this.showWarningModal = true;
      switch (this.productsError) {
        case 'invalid_product_amount':
          this.productWarningBody = this.$t('errors.invalid_product_amount');
          break;
        case 'product_amount_lower_than_permits_count':
          this.productWarningBody = this.$t('errors.product_amount_lower_than_permits_count');
          break;
        case 'permit_count_cannot_be_less_than_issued_permits':
          this.productWarningBody = this.$t('errors.permit_count_cannot_be_less_than_issued_permits');
          break;
        case 'poolparking_allowed_only_permitPack':
          this.productWarningBody = this.$t('errors.poolparking_allowed_only_permitPack');
          break;
        case 'cannot_remove_product_with_issued_permits':
          this.productWarningBody = this.$t('errors.cannot_remove_product_with_issued_permits');
          break;
        case 'partner_product_relation_already_exists':
          this.productWarningBody = this.$t('errors.partner_product_relation_already_exists');
          break;
        default:
          this.productWarningBody = this.$t('errors.zone_capacity_exceeded');
      }
    },
    editTotalPermitAmount(prop, value) {
      this[prop] = value;
      this.permit_value = this[prop];
    },
    editPoolParkingLimitExceedingAmount(prop, value) {
      this[prop] = value;
      this.pool_parking = this[prop];
    },
    editTenantPays(value) {
      this['tenantPaysPpInput'] = value;
      this.tenant_pays_pp = value;
    },
    fetchTenant() {
      this.$store.dispatch('tenants/tenant/fetch', { id: this.$route.params.id, user: this.user });
    },
    fetchTenantList() {
      this.$store.dispatch('landlord/tenants/fetch', { user: this.user, limit: this.tenantLimit, keyword: this.keywordFilter });
    },
    setSelectedRow(row, name = '') {
      let rowId = row && row.id ? row.id : row;
      rowId = name ? `${rowId}${name}` : rowId
      this.selectedRow.id = rowId;
      this.selectedRow.tenantId = this.tenant.id;
      this.$nextTick(() => {
        if (this.isSelectedRow(rowId)) {
          if (this.$refs[`changeIssued${rowId}`]) {
            this.$refs[`changeIssued${rowId}`].focus();
          } else if (this.$refs[`changePoolParking${rowId}`]) {
            this.$refs[`changePoolParking${rowId}`].focus();
          }
        }
      });
      this.oldValue = row && row.issued ? row.issued : row;
      this.oldPoolParkingValue = row && row.pool_parking ? row.pool_parking : row;
      this.oldTenantPaysPpValue = row && row.tenant_pays_pp ? row.tenant_pays_pp : row;
    },
    isSelectedRow(rowId) {
      if (this.selectedRow.tenantId !== this.tenant.id) {
        this.setSelectedRow(null);
      }
      return this.selectedRow.id === rowId;
    },
    createAmountValidator() {
      this.$validator.extend('cannotBeLower', {
        getMessage: () => 'Number cannot be lower then assigned permits',
        validate: (value) => {
          if (value < 1) {
            return false;
          }
          return true;
        },
      });
    },
    isLengthSmaller(index, length) {
      return index < (length - 1);
    },
    routeToProduct(product) {
      this.$router.push({ name: 'product', params: { id: product.id }, query: { keyword: product.name } });
    },
  },
  data() {
    return {
      showConfirmationModal: false,
      showWarningModal: false,
      removeProduct: null,
      permitInput: null,
      poolParkingInput: null,
      tenantPaysPpInput: null,
      isEditingTenant: false,
      isActive: false,
      isRemoved: false,
      isProductFormOpened: false,
      product: null,
      oldValue: null,
      oldPoolParkingValue: null,
      oldTenantPaysPpValue: null,
      form: {
        amount: null,
        pool_parking: null,
        tenant_pays_pp: null
      },
      selectedRow: {
        id: null,
        tenantId: null,
      },
      columns: ['type', 'name', 'zone', 'available', 'issued', 'pool_parking', 'tenant_pays_pp', 'remove_product', 'edit'],
      options: {
        texts: {
          noResults: this.$t('tenants.no_matching_records'),
          count: this.$t('tables.count'),
        },
        perPage: 10,
        filterable: false,
        rowClassCallback: () => 'VueTables__row',
        headings: {
          type: () => this.$t('products.type'),
          name: () => this.$t('common.name'),
          zone: () => this.$t('common.zone'),
          available: () => this.$t('products.parking_spots'),
          issued: () => this.$t('common.issued'),
          pool_parking: () => this.user.poolParkingEnabled ? this.$t('common.pool_parking') : null,
          tenant_pays_pp: () => this.user.poolParkingEnabled ? this.$t('common.tenant_pays_pp') : null,
          remove_product: () => (''),
          edit: () => (''),
        },
      },
    };
  },
};
</script>
<style lang="scss" scoped>
@import '~styles/blocks/details.scss';
@import '~styles/app.scss';

.details-component__listWidget {
  width: 50%;
}

.link {
  .icon--plus {
    position: relative;
    top: -1px;
    margin: 0 5px;
    font-weight: 900;
  }
}

.shortInput {
  width: 70px;
}

.span-in-row {
  display: flex;
}

.form-in-row {
  display: flex;
  justify-content: start;
  justify-items: center;

  & span {
    display: flex;
    padding-left: 5px;
  }

  .form__submit-edit {
    margin-left: 50px;
    margin-top: -11px;
  }
}

.tenantPays {
  background-image: url('../../assets/icons/done.svg');
  background-size: 20px 40px;
  background-repeat: no-repeat;
  background-position: center center;
  height: 25px;
  width: 25px;
  display: block;
}
</style>



// WEBPACK FOOTER //
// src/components/tenants/ProductsTable.vue